import { createContext, use, useCallback } from 'react';
import { useLocation } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';

import { useSSRState } from '@/hooks/use-ssr-state.ts';
import { InAppGateContext } from '@/lib/in-app-gate.tsx';

import { useAuth } from '../Auth/index.ts';
import { DOMAIN } from '../Config.js';

export type ChatOptions = {
  goal?: string;
  step?: string;
  clientKnowledge?: Record<string, any>;
};

export interface BrainSessionContextType {
  chatOptions: ChatOptions | undefined;
  showChatContainer: boolean;
  closeModalUrl: string | undefined;
  userId: string | undefined;
  toggleChat: (options: ChatOptions) => void;
  closeChatModal: () => void;
  saveCloseModalUrl: (url: string | null) => void;
}

const BrainSessionContext = createContext<BrainSessionContextType>(undefined!);

interface BrainSessionProviderProps {
  children?: React.ReactNode;
}

function BrainSessionProvider({ children = null }: BrainSessionProviderProps) {
  const location = useLocation();
  const [chatOptions, setChatOptions] = useSSRState<ChatOptions | undefined>({
    // Prerendered HTML is always rendered for a URL without a query string, so the hydration
    // pass has to ignore `?flow=&step=` deep links. They take effect on the render right after.
    server: () => undefined,
    client: () => {
      const params = new URLSearchParams(location.search);
      const step = params.get('step');
      const goal = params.get('flow');
      return goal && step ? { step, goal } : undefined;
    },
  });
  const { isInAppBrowser } = use(InAppGateContext);
  const showChatContainer = Boolean(chatOptions);
  const [closeModalUrl, setCloseModalUrl, removeCloseModalUrl] = useLocalStorage<
    string | undefined
  >(DOMAIN.closeModalUrl, undefined, {
    initializeWithValue: false,
  });
  const { userId } = useAuth();

  const saveCloseModalUrl = useCallback(
    (url: string | null) => {
      if (url === null) {
        removeCloseModalUrl();
      } else {
        setCloseModalUrl(url);
      }
    },
    [setCloseModalUrl, removeCloseModalUrl],
  );

  // TODO: rename to `showChat`
  const toggleChat = useCallback(
    async (options: ChatOptions) => {
      if (await isInAppBrowser()) {
        return;
      }
      setChatOptions(options);
    },
    [isInAppBrowser, setChatOptions],
  );

  const closeChatModal = () => {
    setChatOptions(undefined);
  };

  return (
    <BrainSessionContext.Provider
      value={{
        chatOptions,
        showChatContainer,
        closeModalUrl,
        userId,
        toggleChat,
        closeChatModal,
        saveCloseModalUrl,
      }}
    >
      {children}
    </BrainSessionContext.Provider>
  );
}

export { BrainSessionContext, BrainSessionProvider };
